import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import PAGES from "@/content/pages.json";
import { ApparatCalc, BenzinCalc, ElbilCalc, VarmeCalc, SolcelleCalc, EnergimaerkeCalc } from "@/components/Calculators";
import PrisOversigt from "@/components/PrisOversigt";

const CALCS = { apparat: <ApparatCalc />, benzin: <BenzinCalc />, elbil: <ElbilCalc />, varme: <VarmeCalc />, solceller: <SolcelleCalc />, energimaerke: <EnergimaerkeCalc />, priser: <PrisOversigt /> };
const CANON = "https://forbrugsregner.dk";

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const stripTags = (s) => s.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();

function extractFaq(html) {
  const out = [];
  const re = /<details><summary>([\s\S]*?)<\/summary><p>([\s\S]*?)<\/p><\/details>/g;
  let m;
  while ((m = re.exec(html))) {
    out.push({
      "@type": "Question",
      name: stripTags(m[1]),
      acceptedAnswer: { "@type": "Answer", text: stripTags(m[2]) },
    });
  }
  return out;
}

function applySeasonal(text, seasonal, active) {
  if (text && seasonal && active && text.includes(seasonal.find)) {
    return text.replace(seasonal.find, seasonal.replace);
  }
  return text;
}

export default function PageView() {
  const location = useLocation();
  let path = location.pathname;
  if (path.length > 1 && !path.endsWith("/")) path += "/";
  const found = Boolean(PAGES[path]);
  const page = found ? PAGES[path] : PAGES["__404__"];

  const seasonOverride = location.search.includes("season=jul") ? 12 : location.search.includes("season=foraar") ? 3 : null;
  const month = seasonOverride || new Date().getMonth() + 1;
  const season = (page.seasons || []).find((s) => s.months.includes(month)) || null;
  const main = applySeasonal(page.main, season, Boolean(season));
  const rest = applySeasonal(page.rest, season, Boolean(season));

  useEffect(() => {
    document.title = page.title;
    upsertMeta("name", "description", page.description || "");
    upsertMeta("property", "og:title", page.title);
    upsertMeta("property", "og:description", page.description || "");
    if (found) {
      upsertMeta("name", "robots", "index,follow,max-image-preview:large");
      upsertLink("canonical", CANON + path);
      upsertMeta("property", "og:url", CANON + path);
      if (page.ogimage) {
        upsertMeta("property", "og:image", page.ogimage);
        upsertMeta("name", "twitter:image", page.ogimage);
      }
    } else {
      upsertMeta("name", "robots", "noindex,follow");
      const c = document.head.querySelector('link[rel="canonical"]');
      if (c) c.remove();
    }

    const old = document.getElementById("faq-jsonld");
    if (old) old.remove();
    const faqs = extractFaq(page.main || (page.hero || "") + (page.rest || ""));
    if (faqs.length) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = "faq-jsonld";
      s.textContent = JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqs });
      document.head.appendChild(s);
    }
  }, [path, page, found]);

  const testid = "page-" + (path === "/" ? "home" : path.replace(/^\/|\/$/g, "").replace(/\//g, "-"));

  if (page.calc) {
    return (
      <main id="main" data-testid={testid}>
        <section className="hero">
          <div className="wrap herogrid">
            <div dangerouslySetInnerHTML={{ __html: page.hero }} />
            <div>{CALCS[page.calc]}</div>
          </div>
        </section>
        <div style={{ display: "contents" }} dangerouslySetInnerHTML={{ __html: rest }} />
      </main>
    );
  }

  return <main id="main" data-testid={testid} dangerouslySetInnerHTML={{ __html: main }} />;
}
